import { getTheme, type Theme } from '../config/themes'

const THEMES_BASE_PATH = '/themes'

// Cache for loaded themes
const themeCache = new Map<string, Theme>()
const loadingThemes = new Set<string>()

// Clear theme cache
export function clearThemeCache(): void {
  themeCache.clear()
  loadingThemes.clear()
}

export async function loadThemeFromFile(themeName: string): Promise<Theme | null> {
  // Check cache first
  const cachedTheme = themeCache.get(themeName)
  if (cachedTheme) {
    return cachedTheme
  }
  
  // If already loading this theme, wait for it
  if (loadingThemes.has(themeName)) {
    while (loadingThemes.has(themeName)) {
      await new Promise(resolve => setTimeout(resolve, 50))
    }
    // Check cache again after waiting
    const cachedThemeAfterWait = themeCache.get(themeName)
    if (cachedThemeAfterWait) {
      return cachedThemeAfterWait
    }
  }
  
  loadingThemes.add(themeName)
  
  try {
    const accountThemePath = `${THEMES_BASE_PATH}/${themeName}/css/custom.json`
    const accountResponse = await fetch(accountThemePath)
    
    if (accountResponse.ok) {
      const themeData = await accountResponse.json()
      
      if (!themeData.name || !themeData.colors || !themeData.typography) {
        throw new Error(`Invalid theme structure: ${accountThemePath}`)
      }
      
      themeData.name = themeName
      const theme = themeData as Theme
      themeCache.set(themeName, theme) // Cache the theme
      loadingThemes.delete(themeName)
      return theme
    }
  } catch {
    // Continue to fallback to flat JSON below
  }
  
  try {
    const response = await fetch(`${THEMES_BASE_PATH}/${themeName}.json`)
    
    if (!response.ok) {
      if (response.status === 404) {
        loadingThemes.delete(themeName)
        return null
      }
      throw new Error(`Failed to load theme: ${response.status}`)
    }
    
    const themeData = await response.json()
    
    if (!themeData.name || !themeData.colors || !themeData.typography) {
      throw new Error(`Invalid theme structure: ${themeName}.json`)
    }
    
    themeData.name = themeName
    const theme = themeData as Theme
    themeCache.set(themeName, theme) // Cache the theme
    loadingThemes.delete(themeName)
    return theme
    
  } catch (error) {
    console.error(`Error loading theme ${themeName}:`, error)
    loadingThemes.delete(themeName)
    return null
  }
}

export function resolveThemeName(
  urlTheme: string | null | undefined
): string {
  if (!urlTheme || urlTheme.trim() === '' || urlTheme.trim().toLowerCase() === 'default') {
    return 'default'
  }
  
  let cleaned = urlTheme.trim()
  
  const queryIndex = cleaned.indexOf('?')
  if (queryIndex !== -1) {
    cleaned = cleaned.substring(0, queryIndex)
  }
  
  const pathIndex = cleaned.indexOf('/')
  if (pathIndex !== -1) {
    cleaned = cleaned.substring(0, pathIndex)
  }
  
  const hashIndex = cleaned.indexOf('#')
  if (hashIndex !== -1) {
    cleaned = cleaned.substring(0, hashIndex)
  }
  
  return cleaned || 'default'
}

export async function loadThemeWithFallback(themeName: string): Promise<Theme> {
  const loadedTheme = await loadThemeFromFile(themeName)
  if (loadedTheme) return loadedTheme
  return getTheme('default')
}

